import {
  cloneElement,
  forwardRef,
  isValidElement,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';

interface FormFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  hint?: string;
  children?: ReactNode;
}

interface ControlElementProps {
  id?: string;
  required?: boolean;
  'aria-invalid'?: boolean | 'true' | 'false';
  'aria-required'?: boolean | 'true' | 'false';
  'aria-describedby'?: string;
}

export const FormField = forwardRef<HTMLInputElement, FormFieldProps>(function FormField(
  { label, error, hint, children, id, className, 'aria-describedby': ariaDescribedBy, ...inputProps },
  ref,
) {
  // Two FormFields sharing a label (e.g. two "Role" selects on one page) would otherwise
  // both resolve to the same fallback id and collide, so every un-id'd field - custom
  // control or plain input alike - falls back to a React-generated id rather than a
  // label-derived slug.
  const generatedId = useId();
  const childElement = isValidElement<ControlElementProps>(children) ? children : null;
  const fieldId = childElement?.props.id ?? id ?? generatedId;
  const errorId = `${fieldId}-error`;
  const hintId = `${fieldId}-hint`;
  const describedBy = [ariaDescribedBy, error && errorId, hint && !error && hintId].filter(Boolean).join(' ') || undefined;
  const isRequired = Boolean(inputProps.required);

  const control = childElement
    ? cloneElement(childElement, {
        id: fieldId,
        required: isRequired || childElement.props.required,
        'aria-required': isRequired || childElement.props['aria-required'] ? true : undefined,
        'aria-invalid': error ? true : childElement.props['aria-invalid'],
        'aria-describedby':
          Array.from(
            new Set(
              [childElement.props['aria-describedby'], describedBy].flatMap((value) => (value ? value.split(' ') : [])),
            ),
          ).join(' ') || undefined,
      } satisfies Partial<ControlElementProps>)
    : null;

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={fieldId} className="text-sm font-medium text-gray-700">
        {label}
        {isRequired && <span className="ml-0.5 text-red-500" aria-hidden="true">*</span>}
      </label>

      {control ?? (
        <input
          id={fieldId}
          ref={ref}
          {...inputProps}
          aria-required={isRequired || undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={[
            'rounded border px-3 py-2 text-sm text-gray-900 placeholder-gray-400',
            'focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400',
            'disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400',
            error
              ? 'border-red-400 bg-red-50 focus:ring-red-400 focus:border-red-400'
              : 'border-gray-200 bg-white',
            className,
          ].filter(Boolean).join(' ')}
        />
      )}

      {hint && !error && <p id={hintId} className="text-xs text-gray-400">{hint}</p>}
      {error && <p id={errorId} role="alert" className="text-xs text-red-500">{error}</p>}
    </div>
  );
});
